import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useDevFieldNotes } from "@/context/DevFieldNotesContext";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Code } from "lucide-react";
import { PERM, hasGlobalPermission } from "@/lib/permissions";

/**
 * "<>" indicator revealing the exact upstream partner-feed path a field is
 * sourced from (see docs/specs/partner-data-dictionary-20260812.md). Visible
 * only to holders of system.dev_tools — everyone else renders nothing, same as `path` being
 * empty (a field with no real feed mapping, e.g. a locally-managed flag).
 *
 * Clicking it opens a dev-only note editor (see DevFieldNotesContext) — a
 * temporary scratchpad for finalizing which fields to keep/expose, not part
 * of the product data model.
 */
export function FeedPathTag({ path, testId }) {
  const { user } = useAuth();
  const { notesByKey, saveNote } = useDevFieldNotes();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  if (!path || !hasGlobalPermission(user, PERM.SYSTEM_DEV_TOOLS)) return null;

  const existingNote = notesByKey?.[path]?.note || "";
  const hasNote = !!existingNote;

  const openDialog = () => {
    setDraft(existingNote);
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveNote(path, draft);
      setOpen(false);
      toast.success(draft.trim() ? "Note saved" : "Note cleared");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save note");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              data-testid={testId || "feed-path-tag"}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openDialog();
              }}
              className={`inline-flex shrink-0 items-center rounded-sm ${
                hasNote ? "text-amber-500 hover:text-amber-600" : "text-muted-foreground/70 hover:text-foreground"
              }`}
            >
              <Code className="h-3 w-3" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs font-mono text-[11px]">
            <div>{path}</div>
            {hasNote && (
              <div className="mt-1 whitespace-pre-wrap border-t border-primary-foreground/30 pt-1 font-sans not-italic">
                {existingNote}
              </div>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="field-note-dialog" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Field note</DialogTitle>
          </DialogHeader>
          <p className="break-all font-mono text-xs text-muted-foreground">{path}</p>
          <Textarea
            data-testid="field-note-textarea"
            rows={5}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Dev note about this field…"
            autoFocus
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button data-testid="field-note-save" onClick={save} disabled={saving}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
